"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.box = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _classnames = _interopRequireDefault(require("classnames"));
var _space = _interopRequireWildcard(require("@ux/space"));
var _options = require("./options");
const _excluded = ["className", "blockAlignChildren", "blockAlignSelf", "blockPadding", "displayType", "inlineAlignChildren", "inlineAlignSelf", "inlinePadding", "gap", "orientation", "stretch", "wrap", "elevation", "rounding", "selected"];
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && Object.prototype.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const UX_BOX_CLASSNAME = 'ux-box';

/**
 * Creates a function which represents a dynamic React component
 *
 * @param {String} TagName - The tag name of the element to use for box
 * @returns {Function} - A function to create a component
 */
function create(TagName) {
  /**
   * Creates a React component using the given tag name
   *
   * @param {Object} props - A props configuration object
   * @param {String} [props.className] - A class name to include
   * @param {Node} [props.children] - Children
   * @param {String} [props.blockAlignChildren] - An alignment enum for children: start, center, end, spaceBetween
   * @param {String} [props.blockAlignSelf] - An alignment enum for self: start, center, end
   * @param {String} [props.blockPadding] - A T-shirt size of padding from @ux/space
   * @param {String} [props.displayType] - CSS display property value
   * @param {String} [props.inlineAlignChildren] - An alignment enum for children: start, center, end
   * @param {String} [props.inlineAlignSelf] - An alignment enum for self: start, center, end
   * @param {String} [props.inlinePadding] - A T-shirt size of padding from @ux/space
   * @param {String} [props.gap] - A T-shirt size of gap from @ux/space
   * @param {String} [props.orientation] - vertical or horizontal
   * @param {Boolean} [props.stretch] - Stretch full width of container
   * @param {Boolean} [props.wrap] - Should contents wrap within container
   * @param {Object} ref - Element reference
   * @returns {React.Component} - A functional React component
   */
  const Box = /*#__PURE__*/_react.default.forwardRef(function Box(_ref, ref) {
    let {
        className,
        blockAlignChildren,
        blockAlignSelf,
        blockPadding,
        displayType = 'flex',
        inlineAlignChildren,
        inlineAlignSelf,
        inlinePadding,
        gap,
        orientation = 'vertical',
        stretch = false,
        wrap = false,
        elevation = 'base',
        rounding,
        selected = false
      } = _ref,
      rest = (0, _objectWithoutProperties2.default)(_ref, _excluded);
    const options = {
      blockAlignChildren,
      blockAlignSelf,
      displayType,
      inlineAlignChildren,
      inlineAlignSelf,
      orientation,
      stretch,
      wrap,
      elevation,
      rounding,
      selected
    };
    const classes = (0, _classnames.default)(className, UX_BOX_CLASSNAME, Object.entries(options).reduce(function boxClass(acc, _ref2) {
      let [key, val] = _ref2;
      if (!val) return acc;
      if (key === 'elevation' && typeof val === 'object') {
        const {
          level,
          interaction
        } = val;
        return _objectSpread(_objectSpread({}, acc), {}, {
          [`${UX_BOX_CLASSNAME}--${key}-${level}`]: level,
          [`${UX_BOX_CLASSNAME}--${key}-${level}--interactive`]: interaction
        });
      }
      return _objectSpread(_objectSpread({}, acc), {}, {
        [`${UX_BOX_CLASSNAME}--${key}-${val}`]: val
      });
    }, {}));
    const Tag = _space.default[TagName];
    return /*#__PURE__*/_react.default.createElement(Tag, (0, _extends2.default)({
      block: blockPadding,
      inline: inlinePadding,
      gap: gap,
      ref: ref,
      className: classes
    }, rest));
  });

  /**
   *
   * @enum {Object} expected types of properties.
   * @public
   */
  Box.propTypes = {
    /** Children for the component */
    children: _propTypes.default.node,
    /** Classes to apply to component */
    className: _propTypes.default.string,
    /** Sets the block-axis alignment of children, accounting for writing-mode. */
    blockAlignChildren: _propTypes.default.oneOf(['start', 'center', 'end', 'spaceBetween']),
    /** Sets the vertical alignment of itself assuming it is a child of another <Box/> */
    blockAlignSelf: _propTypes.default.oneOf(_options.BOX_ALIGNMENTS),
    /** Expects a abbreviated T-shirt size for the padding to be applied around children in the block axis. */
    blockPadding: _propTypes.default.oneOf(Object.values(_space.sizeOptions)),
    /** A CSS display property, defaults to 'flex'. */
    displayType: _propTypes.default.oneOf(['flex', 'inline-flex']),
    /** Sets the horizontal alignment of children and accounting for writing-mode. */
    inlineAlignChildren: _propTypes.default.oneOf(_options.BOX_ALIGNMENTS),
    /** Sets the inline-axis alignment of itself assuming it is a child of another <Box/> */
    inlineAlignSelf: _propTypes.default.oneOf(_options.BOX_ALIGNMENTS),
    /** Expects a abbreviated T-shirt size for the padding to be applied around children in the inline axis. */
    inlinePadding: _propTypes.default.oneOf(Object.values(_space.sizeOptions)),
    /** Expects a abbreviated T-shirt size for the gap to be applied between children. */
    gap: _propTypes.default.oneOf(Object.values(_space.sizeOptions)),
    /** How children appear within the component on a single axis.
     * Default is vertical to suggest block stacking behavior of normal <div/> elements. */
    orientation: _propTypes.default.oneOf(['vertical', 'horizontal']),
    /** Stretches the available space, similar to flex: 1 as appropriate. */
    stretch: _propTypes.default.bool,
    /** Should children wrap when room isn't available.
     * Does not produce results with orientation='vertical'. */
    wrap: _propTypes.default.bool,
    /** The elevation level of the box; defaults to base.
     * Setting this to an object allows for interactivity via an interaction boolean. */
    elevation: _propTypes.default.oneOfType([_propTypes.default.oneOf(_options.ELEVATION_LEVELS), _propTypes.default.shape({
      level: _propTypes.default.oneOf(_options.ELEVATION_LEVELS),
      interaction: _propTypes.default.bool
    })]),
    /** How rounded the box should be; one of { 'none', 'reduced' } */
    rounding: _propTypes.default.oneOf(['none', 'reduced']),
    /**	Whether the box has been selected, applicable only to cards. */
    selected: _propTypes.default.bool
  };
  return Box;
}
const box = exports.box = new Proxy(function boxProxy() {}, {
  get: function get(target, prop) {
    // Deliberately not using hasOwnProperty to catch things like __proto__
    if (!String(prop).startsWith('_') && typeof target[prop] === 'undefined') {
      // Cache access by creating a key with value
      target[prop] = create(prop);
    }
    // Always return thing at that key, either undefined or thing set
    return Reflect.get(...arguments);
  }
});
var _default = exports.default = box.div;